import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {Activity, BreakKey, HubblFilter, HubblTask, User} from 'hubbl-shared';
import {HubblBreakDTO, WebappHubbl} from '../../models';
import {
  acceptBreak,
  addBreakTask,
  addHubblGroupTask,
  createHubblConnect,
  createHubblGroup,
  declineBreak,
  declineHubbl,
  deleteBreakTask,
  deleteHubblGroupTask,
  fetchPendingBreaks,
  fetchPendingHubbles,
  fetchScheduledBreaks,
  fetchScheduledHubbles,
  joinHubblGroup,
  leaveHubblGroup,
  undeclineBreak,
  undeclineHubbl,
  updateBreakTask,
  updateHubblGroupTask,
} from './HubblThunks';

// Helper function to check if a hubbl is a break
const isBreak = (hubbl: WebappHubbl): hubbl is HubblBreakDTO => {
  return BreakKey.UserCreator in hubbl;
};

interface HubblState {
  // Viewing Hubbles
  pendingHubbles: {
    hubbles: WebappHubbl[];
    loading: boolean;
    error: string | null;
    hasLoaded: boolean;
  };
  scheduledHubbles: {
    hubbles: WebappHubbl[];
    loading: boolean;
    error: string | null;
    hasLoaded: boolean;
  };

  // Hubbl Creation Flow
  creation: {
    type: 'connect' | 'group' | null;
    connect: {
      activities: Activity[];
      invitedUsers: User[];
      filters: HubblFilter[];
      tasks: HubblTask[];
    };
    group: {
      invitedUsers: User[];
      filters: HubblFilter[];
      tasks: HubblTask[];
    };
    loading: boolean;
    error: string | null;
  };

  // Individual Hubbl management
  operations: {
    loading: boolean;
    error: string | null;
  };
}

const initialState: HubblState = {
  pendingHubbles: {
    hubbles: [],
    loading: false,
    error: null,
    hasLoaded: false,
  },
  scheduledHubbles: {
    hubbles: [],
    loading: false,
    error: null,
    hasLoaded: false,
  },

  creation: {
    type: null,
    connect: {
      activities: [],
      invitedUsers: [],
      filters: [],
      tasks: [],
    },
    group: {
      invitedUsers: [],
      filters: [],
      tasks: [],
    },
    loading: false,
    error: null,
  },
  operations: {
    loading: false,
    error: null,
  },
};

const hubblSlice = createSlice({
  name: 'hubbl',
  initialState,
  reducers: {
    // Creation flow actions
    setCreationType: (
      state,
      action: PayloadAction<'connect' | 'group' | null>,
    ) => {
      state.creation.type = action.payload;
    },

    // Connect creation
    setConnectActivities: (state, action: PayloadAction<Activity[]>) => {
      state.creation.connect.activities = action.payload;
    },
    setConnectInvitedUsers: (state, action: PayloadAction<User[]>) => {
      state.creation.connect.invitedUsers = action.payload;
    },
    setConnectFilters: (state, action: PayloadAction<HubblFilter[]>) => {
      state.creation.connect.filters = action.payload;
    },
    setConnectTasks: (state, action: PayloadAction<HubblTask[]>) => {
      state.creation.connect.tasks = action.payload;
    },

    // Group creation
    setGroupInvitedUsers: (state, action: PayloadAction<User[]>) => {
      state.creation.group.invitedUsers = action.payload;
    },
    setGroupFilters: (state, action: PayloadAction<HubblFilter[]>) => {
      state.creation.group.filters = action.payload;
    },
    setGroupTasks: (state, action: PayloadAction<HubblTask[]>) => {
      state.creation.group.tasks = action.payload;
    },

    // Reset creation flow
    resetCreationFlow: state => {
      state.creation = initialState.creation;
    },

    // Clear errors
    clearError: state => {
      state.creation.error = null;
      state.operations.error = null;
      state.pendingHubbles.error = null;
      state.scheduledHubbles.error = null;
    },

    // Reset all hubbles
    resetHubbles: state => {
      state.pendingHubbles = initialState.pendingHubbles;
      state.scheduledHubbles = initialState.scheduledHubbles;
    },
  },
  extraReducers: builder => {
    // Fetch pending hubbles
    builder
      .addCase(fetchPendingHubbles.pending, state => {
        state.pendingHubbles.loading = true;
        state.pendingHubbles.error = null;
      })
      .addCase(fetchPendingHubbles.fulfilled, (state, action) => {
        state.pendingHubbles.loading = false;

        if (action.payload.isAppend) {
          // Append mode: add to existing array
          state.pendingHubbles.hubbles = [
            ...state.pendingHubbles.hubbles,
            ...action.payload.hubbles,
          ];
        } else {
          // Replace mode: keep existing breaks, replace only hubbles
          const existingBreaks = state.pendingHubbles.hubbles.filter(hubbl =>
            isBreak(hubbl),
          );
          state.pendingHubbles.hubbles = [
            ...existingBreaks,
            ...action.payload.hubbles,
          ];
        }

        state.pendingHubbles.hasLoaded = true;
      })
      .addCase(fetchPendingHubbles.rejected, (state, action) => {
        state.pendingHubbles.loading = false;
        state.pendingHubbles.error =
          action.error.message || 'Failed to fetch pending hubbles';
      })

      // Fetch scheduled hubbles
      .addCase(fetchScheduledHubbles.pending, state => {
        state.scheduledHubbles.loading = true;
        state.scheduledHubbles.error = null;
      })
      .addCase(fetchScheduledHubbles.fulfilled, (state, action) => {
        state.scheduledHubbles.loading = false;
        // Keep existing breaks, replace only hubbles
        const existingBreaks = state.scheduledHubbles.hubbles.filter(hubbl =>
          isBreak(hubbl),
        );
        state.scheduledHubbles.hubbles = [...existingBreaks, ...action.payload];
        state.scheduledHubbles.hasLoaded = true;
      })
      .addCase(fetchScheduledHubbles.rejected, (state, action) => {
        state.scheduledHubbles.loading = false;
        state.scheduledHubbles.error =
          action.error.message || 'Failed to fetch scheduled hubbles';
      })

      // Fetch pending breaks
      .addCase(fetchPendingBreaks.pending, state => {
        // Don't set loading = true here since it's shared with fetchPendingHubbles
        // Just clear any previous error
        state.pendingHubbles.error = null;
      })
      .addCase(fetchPendingBreaks.fulfilled, (state, action) => {
        // Don't set loading = false here since it's shared with fetchPendingHubbles
        // Don't set hasLoaded = true here since it's shared with fetchPendingHubbles
        // Just add breaks to the existing hubbles array
        const existingHubbles = state.pendingHubbles.hubbles.filter(
          hubbl => !isBreak(hubbl),
        );
        state.pendingHubbles.hubbles = [...existingHubbles, ...action.payload];
      })
      .addCase(fetchPendingBreaks.rejected, (state, action) => {
        state.pendingHubbles.loading = false;
        state.pendingHubbles.error =
          action.error.message || 'Failed to fetch pending breaks';
      })

      // Fetch scheduled breaks
      .addCase(fetchScheduledBreaks.pending, state => {
        // Don't set loading = true here since it's shared with fetchScheduledHubbles
        // Just clear any previous error
        state.scheduledHubbles.error = null;
      })
      .addCase(fetchScheduledBreaks.fulfilled, (state, action) => {
        // Don't set loading = false here since it's shared with fetchScheduledHubbles
        // Don't set hasLoaded = true here since it's shared with fetchScheduledHubbles
        // Just add breaks to the existing hubbles array
        const existingHubbles = state.scheduledHubbles.hubbles.filter(
          hubbl => !isBreak(hubbl),
        );
        state.scheduledHubbles.hubbles = [
          ...existingHubbles,
          ...action.payload,
        ];
      })
      .addCase(fetchScheduledBreaks.rejected, (state, action) => {
        state.scheduledHubbles.loading = false;
        state.scheduledHubbles.error =
          action.error.message || 'Failed to fetch scheduled breaks';
      })

      // Create hubbl group
      .addCase(createHubblGroup.pending, state => {
        state.creation.loading = true;
        state.creation.error = null;
      })
      .addCase(createHubblGroup.fulfilled, (state, action) => {
        state.creation.loading = false;
        // Add the new hubbl to the pending list if it matches current filters
        if (action.payload) {
          state.pendingHubbles.hubbles = [
            action.payload,
            ...state.pendingHubbles.hubbles,
          ];
        }
      })
      .addCase(createHubblGroup.rejected, (state, action) => {
        state.creation.loading = false;
        state.creation.error =
          action.error.message || 'Failed to create hubbl group';
      })

      // Create hubbl connect
      .addCase(createHubblConnect.pending, state => {
        state.creation.loading = true;
        state.creation.error = null;
      })
      .addCase(createHubblConnect.fulfilled, (state, action) => {
        state.creation.loading = false;
        // Add the new break to the pending list if it matches current filters
        if (action.payload) {
          state.pendingHubbles.hubbles = [
            action.payload,
            ...state.pendingHubbles.hubbles,
          ];
        }
      })
      .addCase(createHubblConnect.rejected, (state, action) => {
        state.creation.loading = false;
        state.creation.error =
          action.error.message || 'Failed to create hubbl connect';
      })

      // Hubbl operations
      .addCase(joinHubblGroup.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(joinHubblGroup.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the hubbl in the list
        const hubblId = action.meta.arg.hubblId;
        const updateHubblInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === hubblId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateHubblInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateHubblInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(joinHubblGroup.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to join hubbl group';
      })

      .addCase(leaveHubblGroup.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(leaveHubblGroup.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the hubbl in the list
        const hubblId = action.meta.arg.hubblId;
        const updateHubblInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === hubblId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateHubblInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateHubblInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(leaveHubblGroup.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to leave hubbl group';
      })

      .addCase(declineHubbl.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(declineHubbl.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the hubbl in the list
        const hubblId = action.meta.arg.hubblId;
        const updateHubblInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === hubblId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateHubblInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateHubblInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(declineHubbl.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to decline hubbl';
      })

      .addCase(acceptBreak.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(acceptBreak.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the break in the list
        const breakId = action.meta.arg.hubblId;
        const updateBreakInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === breakId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateBreakInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateBreakInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(acceptBreak.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to accept break';
      })

      .addCase(declineBreak.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(declineBreak.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the break in the list
        const breakId = action.meta.arg.hubblId;
        const updateBreakInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === breakId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateBreakInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateBreakInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(declineBreak.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to decline break';
      })

      .addCase(undeclineHubbl.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(undeclineHubbl.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the hubbl in the list
        const hubblId = action.meta.arg.hubblId;
        const updateHubblInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === hubblId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateHubblInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateHubblInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(undeclineHubbl.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to undecline hubbl';
      })

      .addCase(undeclineBreak.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(undeclineBreak.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the break in the list
        const breakId = action.meta.arg.hubblId;
        const updateBreakInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === breakId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateBreakInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateBreakInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(undeclineBreak.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to undecline break';
      })

      // Task management for HubblGroups
      .addCase(addHubblGroupTask.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(addHubblGroupTask.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the hubbl in the list
        const hubblId = action.meta.arg.hubblId;
        const updateHubblInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === hubblId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateHubblInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateHubblInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(addHubblGroupTask.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error = action.error.message || 'Failed to add task';
      })

      .addCase(updateHubblGroupTask.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(updateHubblGroupTask.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the hubbl in the list
        const hubblId = action.meta.arg.hubblId;
        const updateHubblInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === hubblId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateHubblInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateHubblInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(updateHubblGroupTask.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to update task';
      })

      .addCase(deleteHubblGroupTask.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(deleteHubblGroupTask.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the hubbl in the list
        const hubblId = action.meta.arg.hubblId;
        const updateHubblInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === hubblId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateHubblInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateHubblInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(deleteHubblGroupTask.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to delete task';
      })

      // Task management for Breaks/Connects
      .addCase(addBreakTask.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(addBreakTask.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the break in the list
        const breakId = action.meta.arg.breakId;
        const updateBreakInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === breakId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateBreakInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateBreakInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(addBreakTask.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to add break task';
      })

      .addCase(updateBreakTask.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(updateBreakTask.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the break in the list
        const breakId = action.meta.arg.breakId;
        const updateBreakInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === breakId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateBreakInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateBreakInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(updateBreakTask.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to update break task';
      })

      .addCase(deleteBreakTask.pending, state => {
        state.operations.loading = true;
        state.operations.error = null;
      })
      .addCase(deleteBreakTask.fulfilled, (state, action) => {
        state.operations.loading = false;
        // Update the break in the list
        const breakId = action.meta.arg.breakId;
        const updateBreakInList = (hubbles: WebappHubbl[]) =>
          hubbles.map(hubbl =>
            hubbl.id === breakId ? {...hubbl, ...action.payload} : hubbl,
          );

        state.pendingHubbles.hubbles = updateBreakInList(
          state.pendingHubbles.hubbles,
        );
        state.scheduledHubbles.hubbles = updateBreakInList(
          state.scheduledHubbles.hubbles,
        );
      })
      .addCase(deleteBreakTask.rejected, (state, action) => {
        state.operations.loading = false;
        state.operations.error =
          action.error.message || 'Failed to delete break task';
      });
  },
});

export const {
  setCreationType,
  setConnectActivities,
  setConnectInvitedUsers,
  setConnectFilters,
  setConnectTasks,
  setGroupInvitedUsers,
  setGroupFilters,
  setGroupTasks,
  resetCreationFlow,
  clearError,
  resetHubbles,
} = hubblSlice.actions;

export const HubblReducer = hubblSlice.reducer;
